import React, { useState, useEffect } from 'react';
import {
    View,
    Text,
    StyleSheet,
    TouchableOpacity,
    useWindowDimensions,
} from 'react-native';
import { IActivity, KeyStage } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { SkiaCanvas } from './SkiaCanvas';
import { useStageTokens } from '../hooks/useStageTokens';
import { getStudentSafeActivityTitle } from '../utils/studentTaskTitle';

interface SpecialWordsActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/**
 * SpecialWordsActivity — the "Flash & Write" mechanic.
 *
 * Phase 1: The tricky word is shown on a large flash card for N seconds.
 * Phase 2: The card flips to show "Now write it!" and the Skia canvas becomes active.
 * Phase 3: Student writes the word on canvas and submits.
 *
 * In landscape mode the flash card collapses to a slim sidebar so the canvas
 * can use the full available height.
 *
 * This is canvas-first in line with the EduWrite vision — handwriting is always
 * the primary capture modality. The AI engine can then evaluate the written attempt.
 */
export const SpecialWordsActivity: React.FC<SpecialWordsActivityProps> = ({ activity, onComplete, keyStage }) => {
    const word: string = (activity.config?.word || '').toLowerCase();
    const flashDurationMs: number = activity.config?.flashDurationMs ?? 3000;
    const tokens = useStageTokens(keyStage);

    const { completedStrokes, clearStrokes } = useStrokeStore();

    const [phase, setPhase] = useState<'flash' | 'write'>('flash');

    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;

    useEffect(() => {
        const timer = setTimeout(() => {
            setPhase('write');
        }, flashDurationMs);
        return () => clearTimeout(timer);
    }, [flashDurationMs]);

    const handleSubmit = () => {
        onComplete();
    };

    // ── Landscape write-phase — slim sidebar + full-width canvas ─────────────
    if (isLandscape && phase === 'write') {
        return (
            <View style={styles.containerLandscape}>
                {/* Slim left column — prompt + controls */}
                <View style={styles.landscapeLeft}>
                    <View style={styles.pillCard}>
                        <Text style={styles.pillPrompt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✏️ Now write it!</Text>
                        <Text style={styles.pillSub} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Write the word from memory</Text>
                    </View>

                    <View style={styles.landscapeControls}>
                        <TouchableOpacity style={styles.clearBtnLandscape} onPress={clearStrokes}
                            accessibilityRole="button"
                            accessibilityLabel="Clear"
                            accessibilityHint="Clears all strokes from the canvas"
                        >
                            <Text style={styles.clearTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Clear</Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.submitBtnLandscape, completedStrokes.length === 0 && styles.disabledBtn]}
                            onPress={handleSubmit}
                            disabled={completedStrokes.length === 0}
                            accessibilityRole="button"
                            accessibilityLabel="Done"
                            accessibilityHint="Submits your handwriting"
                            accessibilityState={{ disabled: completedStrokes.length === 0 }}
                        >
                            <Text style={styles.submitTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Canvas fills the rest */}
                <View style={styles.canvasWrapperLandscape}
                    accessibilityRole="image"
                    accessibilityLabel={`Handwriting canvas for writing the word ${word}`}
                >
                    <SkiaCanvas />
                    {/* Teacher Feedback Overlay — inside wrapper to ensure alignment */}
                    <View style={StyleSheet.absoluteFill} pointerEvents="none">
                        <SkiaCanvas readOnly={true} strokes={[]} />
                    </View>
                </View>
            </View>
        );
    }

    // ── Portrait (or flash phase) — original stacked layout ─────────────────
    return (
        <View style={styles.container}>
            {/* Header — hidden in write phase so the title doesn't reveal the answer */}
            {phase === 'flash' && (
                <View style={styles.header}>
                    <View style={styles.headerRow}>
                        <Text style={[styles.title, { fontSize: tokens.titleFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{getStudentSafeActivityTitle(activity)}</Text>
                    </View>
                    {activity.instructions && (
                        <Text style={[styles.instructions, { fontSize: tokens.bodyFontSize }]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>
                    )}
                </View>
            )}

            {/* Flash card */}
            <View testID="flash-card" style={[styles.flashCard, phase === 'write' && styles.flashCardDone]}>
                {phase === 'flash' ? (
                    <>
                        <Text style={styles.flashLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Remember this word:</Text>
                        <Text style={styles.flashWord} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{word}</Text>
                        <Text style={styles.flashSub} allowFontScaling={true} maxFontSizeMultiplier={1.5}>It will hide in a moment...</Text>
                    </>
                ) : (
                    <>
                        <Text style={styles.writePrompt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Now write it! ✏️</Text>
                        <Text style={styles.writeSub} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Use your best handwriting</Text>
                    </>
                )}
            </View>

            {/* Canvas area — active only in write phase */}
            {phase === 'write' && (
                <View style={styles.canvasWrapper}
                    accessibilityRole="image"
                    accessibilityLabel={`Handwriting canvas for writing the word ${word}`}
                >
                    <SkiaCanvas />
                    {/* Teacher Feedback Overlay — inside wrapper to ensure alignment */}
                    <View style={StyleSheet.absoluteFill} pointerEvents="none">
                        <SkiaCanvas readOnly={true} strokes={[]} />
                    </View>
                </View>
            )}

            {/* Controls */}
            {phase === 'write' && (
                <View style={styles.footer}>
                    <TouchableOpacity style={styles.clearBtn} onPress={clearStrokes}
                        accessibilityRole="button"
                        accessibilityLabel="Clear"
                        accessibilityHint="Clears all strokes from the canvas"
                    >
                        <Text style={styles.clearTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Clear</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.submitBtn, completedStrokes.length === 0 && styles.disabledBtn]}
                        onPress={handleSubmit}
                        disabled={completedStrokes.length === 0}
                        accessibilityRole="button"
                        accessibilityLabel="Done"
                        accessibilityHint="Submits your handwriting"
                        accessibilityState={{ disabled: completedStrokes.length === 0 }}
                    >
                        <Text style={styles.submitTxt} allowFontScaling={true} maxFontSizeMultiplier={1.5}>DONE! ➔</Text>
                    </TouchableOpacity>
                </View>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    // ── Portrait ──────────────────────────────────────────────────────────────
    container: { flex: 1, backgroundColor: '#fff', borderRadius: 16 },
    header: { padding: 20, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
    headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 },
    title: { fontSize: 24, fontWeight: 'bold', color: '#1E293B', marginBottom: 4 },
    instructions: { fontSize: 16, color: '#64748B', fontStyle: 'italic' },

    flashCard: {
        alignSelf: 'center', width: '90%', paddingVertical: 20, paddingHorizontal: 16,
        backgroundColor: '#1E1B4B', borderRadius: 20,
        alignItems: 'center', justifyContent: 'center',
        marginTop: 12, marginBottom: 8,
        shadowColor: '#000', shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.25, shadowRadius: 8, elevation: 4,
    },
    flashCardDone: { backgroundColor: '#F0FDF4' },
    flashLabel: { fontSize: 13, color: '#A5B4FC', fontWeight: '600', marginBottom: 8, letterSpacing: 1 },
    flashWord: { fontSize: 44, fontWeight: 'bold', color: '#FFFFFF', letterSpacing: 4, marginBottom: 8, textAlign: 'center' },
    flashSub: { fontSize: 13, color: '#818CF8' },
    writePrompt: { fontSize: 24, fontWeight: 'bold', color: '#166534', marginBottom: 4 },
    writeSub: { fontSize: 14, color: '#4B5563' },

    canvasWrapper: { flex: 1, marginHorizontal: 12, marginVertical: 6, borderRadius: 12, overflow: 'hidden' },

    footer: {
        flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 10, gap: 10,
        borderTopWidth: 1, borderTopColor: '#F1F5F9',
    },
    clearBtn: {
        flex: 1, height: 56, minHeight: 52, borderRadius: 12, borderWidth: 1, borderColor: '#CBD5E1',
        alignItems: 'center', justifyContent: 'center',
    },
    clearTxt: { fontSize: 15, fontWeight: '600', color: '#64748B' },
    submitBtn: {
        flex: 2,
        height: 56,
        minHeight: 52,
        backgroundColor: '#22c55e',
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 3 },
        shadowOpacity: 0.3,
        shadowRadius: 6,
        elevation: 4,
    },
    disabledBtn: { backgroundColor: '#D1D5DB' },
    submitTxt: { fontSize: 18, fontWeight: '900', color: '#fff', letterSpacing: 0.5 },

    // ── Landscape write-phase ─────────────────────────────────────────────────
    containerLandscape: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: '#fff',
        borderRadius: 16,
    },

    /** Slim left sidebar — fixed ~170 dp, holds the prompt pill + action buttons */
    landscapeLeft: {
        width: 170,
        backgroundColor: '#F8FAFC',
        borderRightWidth: 1,
        borderRightColor: '#E2E8F0',
        paddingVertical: 12,
        paddingHorizontal: 10,
        justifyContent: 'space-between',
    },

    pillCard: {
        backgroundColor: '#F0FDF4',
        borderRadius: 16,
        padding: 12,
        alignItems: 'center',
        gap: 4,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 6,
        elevation: 2,
    },
    pillPrompt: {
        fontSize: 14,
        fontWeight: '700',
        color: '#166534',
        textAlign: 'center',
    },
    pillWord: {
        fontSize: 28,
        fontWeight: 'bold',
        color: '#166534',
        letterSpacing: 3,
        textAlign: 'center',
    },
    pillSub: {
        fontSize: 11,
        color: '#6B7280',
        textAlign: 'center',
    },

    landscapeControls: {
        gap: 8,
    },
    clearBtnLandscape: {
        height: 56,
        minHeight: 56,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        alignItems: 'center',
        justifyContent: 'center',
    },
    submitBtnLandscape: {
        height: 56,
        minHeight: 56,
        backgroundColor: '#10B981',
        borderRadius: 10,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.2,
        shadowRadius: 6,
        elevation: 3,
    },

    /** Canvas fills all remaining horizontal space */
    canvasWrapperLandscape: {
        flex: 1,
        margin: 8,
        borderRadius: 12,
        overflow: 'hidden',
    },
});
